import React, { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
    CheckSquare, Users, Package, Wifi, Clock, AlertCircle, CheckCircle,
    Phone, TrendingUp, Bell, FileText, Ticket, ChevronRight,
    UserCheck, ArrowRight, Activity, XCircle,
} from 'lucide-react'
import {
    getDashboardStats, getTasks, getUsers, updateTask,
    getCallLogs, getTickets,
} from '../../services/staffServices'
import StaffLayout from './components/StaffLayout'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import toast from 'react-hot-toast'
import { motion, FadeUp, StaggerContainer, StaggerItem, CountUp } from './components/StaffAnimations'

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
    PENDING: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    IN_PROGRESS: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    COMPLETED: 'bg-green-500/15 text-green-400 border-green-500/20',
    CANCELLED: 'bg-red-500/15 text-red-400 border-red-500/20',
}
const STATUS_CYCLE = { PENDING: 'IN_PROGRESS', IN_PROGRESS: 'COMPLETED', COMPLETED: 'PENDING', CANCELLED: 'PENDING' }
const STATUS_ICONS = { PENDING: AlertCircle, IN_PROGRESS: Clock, COMPLETED: CheckCircle, CANCELLED: XCircle }
const PRIORITY_DOT = { LOW: 'bg-green-400', MEDIUM: 'bg-amber-400', HIGH: 'bg-red-400', URGENT: 'bg-purple-400' }

const STAFF_STATUS_COLORS = {
    AVAILABLE: 'bg-green-400',
    BUSY: 'bg-amber-400',
    ON_LEAVE: 'bg-red-400',
    OFFLINE: 'bg-slate-500',
}

function timeAgo(iso) {
    const diff = (Date.now() - new Date(iso)) / 1000
    if (diff < 60) return 'just now'
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
}

function UserAvatar({ user, size = 6 }) {
    const colors = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-amber-600', 'bg-rose-600', 'bg-indigo-600']
    const colorIdx = user?.name ? user.name.charCodeAt(0) % colors.length : 0
    const px = size * 4
    const textSizes = { 6: 'text-[10px]', 8: 'text-xs' }
    const textSize = textSizes[size] || 'text-xs'
    if (user?.profile_photo_url) {
        return (
            <div className="rounded-full overflow-hidden shrink-0 bg-slate-800" style={{ width: px, height: px }}>
                <img src={user.profile_photo_url} alt={user.name} className="w-full h-full object-cover object-top" />
            </div>
        )
    }
    return (
        <div className={`rounded-full ${colors[colorIdx]} flex items-center justify-center font-bold text-white shrink-0 ${textSize}`} style={{ width: px, height: px }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
        </div>
    )
}

// ── Stat Card ─────────────────────────────────────────────────────────────────

const ICON_COLORS = {
    blue: 'bg-blue-600/15 text-blue-400',
    green: 'bg-green-600/15 text-green-400',
    amber: 'bg-amber-600/15 text-amber-400',
    red: 'bg-red-600/15 text-red-400',
    purple: 'bg-purple-600/15 text-purple-400',
    teal: 'bg-teal-600/15 text-teal-400',
}

function StatCard({ icon: Icon, label, value, sub, color = 'blue', to, urgent }) {
    const content = (
        <div className={`bg-slate-900 rounded-xl border p-4 flex flex-col gap-3 hover:border-white/20 hover:shadow-lg hover:shadow-black/20 transition-all duration-200 group ${urgent ? 'border-red-500/30' : 'border-white/5'}`}>
            <div className="flex items-start justify-between">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${ICON_COLORS[color]}`}>
                    <Icon size={18} />
                </div>
                {to && <ChevronRight size={14} className="text-slate-600 group-hover:text-slate-400 transition mt-1" />}
            </div>
            <div>
                <p className="text-2xl font-bold text-white">
                    {value != null && typeof value === 'number' && !sub ? <CountUp value={value} /> : value ?? '—'}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">{label}</p>
                {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
            </div>
        </div>
    )
    return to ? <Link to={to}>{content}</Link> : content
}

// ── Quick Action Button ───────────────────────────────────────────────────────

function QuickAction({ icon: Icon, label, to, color = 'blue' }) {
    const colors = {
        blue: 'bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border-blue-500/20',
        green: 'bg-green-600/10 hover:bg-green-600/20 text-green-400 border-green-500/20',
        purple: 'bg-purple-600/10 hover:bg-purple-600/20 text-purple-400 border-purple-500/20',
        amber: 'bg-amber-600/10 hover:bg-amber-600/20 text-amber-400 border-amber-500/20',
        teal: 'bg-teal-600/10 hover:bg-teal-600/20 text-teal-400 border-teal-500/20',
        rose: 'bg-rose-600/10 hover:bg-rose-600/20 text-rose-400 border-rose-500/20',
    }
    return (
        <Link
            to={to}
            className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border transition ${colors[color]}`}
        >
            <Icon size={18} />
            <span className="text-[10px] font-medium text-center leading-tight">{label}</span>
        </Link>
    )
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()

    const { data: stats, isLoading: statsLoading } = useQuery({
        queryKey: ['dashboard-stats'],
        queryFn: getDashboardStats,
        refetchInterval: 30000,
    })

    const { data: tasks = [] } = useQuery({
        queryKey: ['tasks'],
        queryFn: () => getTasks(),
        refetchInterval: 30000,
    })

    const { data: users = [] } = useQuery({
        queryKey: ['users'],
        queryFn: getUsers,
        refetchInterval: 60000,
    })

    const { data: callLogs = [] } = useQuery({
        queryKey: ['call-logs'],
        queryFn: () => getCallLogs(),
        refetchInterval: 30000,
    })

    const { data: tickets = [] } = useQuery({
        queryKey: ['tickets'],
        queryFn: getTickets,
        refetchInterval: 30000,
    })

    const cycleMut = useMutation({
        mutationFn: ({ id, status }) => updateTask(id, { status }),
        onSuccess: () => qc.invalidateQueries(['tasks']),
        onError: e => toast.error(e.message),
    })

    // ── Derived data ──────────────────────────────────────────────────────────

    const today = new Date().toISOString().split('T')[0]

    const overdueTasks = useMemo(() =>
        tasks.filter(t =>
            t.due_date < today &&
            t.status !== 'COMPLETED' &&
            t.status !== 'CANCELLED'
        ).sort((a, b) => new Date(a.due_date) - new Date(b.due_date)),
        [tasks, today]
    )

    const myTasks = useMemo(() =>
        tasks.filter(t =>
            t.assigned_to === profile?.id &&
            t.status !== 'COMPLETED' &&
            t.status !== 'CANCELLED'
        ).sort((a, b) => {
            const p = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }
            return (p[a.priority] ?? 2) - (p[b.priority] ?? 2)
        }),
        [tasks, profile]
    )

    const recentActivity = useMemo(() => {
        const items = [
            ...tasks.slice(0, 20).map(t => ({ type: 'task', time: t.created_at, data: t })),
            ...callLogs.slice(0, 10).map(c => ({ type: 'call', time: c.created_at, data: c })),
            ...tickets.slice(0, 10).map(t => ({ type: 'ticket', time: t.created_at, data: t })),
        ]
        return items
            .sort((a, b) => new Date(b.time) - new Date(a.time))
            .slice(0, 12)
    }, [tasks, callLogs, tickets])

    const followUpCalls = useMemo(() =>
        callLogs.filter(c => c.needs_follow_up && c.call_status !== 'RESOLVED' && c.call_status !== 'CLOSED')
            .slice(0, 5),
        [callLogs]
    )

    const openTickets = useMemo(() =>
        tickets.filter(t => t.status !== 'CLOSED' && t.status !== 'RESOLVED').slice(0, 5),
        [tickets]
    )

    const staffByStatus = useMemo(() => {
        const groups = {}
        for (const u of users) {
            const s = u.status || 'OFFLINE'
            if (!groups[s]) groups[s] = []
            groups[s].push(u)
        }
        return groups
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users])

    const greeting = useMemo(() => {
        const h = new Date().getHours()
        if (h < 12) return 'Good morning'
        if (h < 17) return 'Good afternoon'
        return 'Good evening'
    }, [])

    return (
        <StaffLayout>
            <div className="space-y-6">

                {/* ── Header ── */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-xl font-bold text-white">
                            {greeting}{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}
                        </h1>
                        <p className="text-slate-400 text-sm mt-0.5">
                            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                    {overdueTasks.length > 0 && (
                        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 text-red-400 text-xs font-medium shrink-0">
                            <AlertCircle size={13} />
                            {overdueTasks.length} overdue
                        </div>
                    )}
                </div>

                {/* ── Quick Actions ── */}
                <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Quick Actions</p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                        <QuickAction icon={CheckSquare} label="New Task" to="/staff/tasks" color="blue" />
                        <QuickAction icon={UserCheck} label="Add Customer" to="/staff/customers" color="green" />
                        <QuickAction icon={Phone} label="Log Call" to="/staff/call-logs" color="amber" />
                        <QuickAction icon={Ticket} label="New Ticket" to="/staff/tickets" color="rose" />
                        <QuickAction icon={FileText} label="Quotation" to="/staff/quotations" color="purple" />
                        <QuickAction icon={Bell} label="Notify Staff" to="/staff/notify" color="teal" />
                    </div>
                </div>

                {/* ── Stats Grid ── */}
                {statsLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Array(8).fill(0).map((_, i) => (
                            <div key={i} className="bg-slate-900 rounded-xl border border-white/5 p-4 animate-pulse h-28" />
                        ))}
                    </div>
                ) : (
                    <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StaggerItem><StatCard icon={AlertCircle} label="Pending Tasks" value={stats?.pendingTasks} color="amber" to="/staff/tasks" urgent={stats?.pendingTasks > 5} /></StaggerItem>
                        <StaggerItem><StatCard icon={Clock} label="In Progress" value={stats?.inProgressTasks} color="blue" to="/staff/tasks" /></StaggerItem>
                        <StaggerItem><StatCard icon={CheckCircle} label="Completed" value={stats?.completedTasks} color="green" to="/staff/tasks" /></StaggerItem>
                        <StaggerItem>
                            <StatCard
                                icon={AlertCircle}
                                label="Overdue"
                                value={overdueTasks.length}
                                color="red"
                                urgent={overdueTasks.length > 0}
                            />
                        </StaggerItem>
                        <StaggerItem><StatCard icon={Users} label="Customers" value={stats?.totalCustomers} color="blue" to="/staff/customers" /></StaggerItem>
                        <StaggerItem><StatCard icon={Wifi} label="BSNL Connections" value={stats?.totalBsnl} color="teal" to="/staff/bsnl" /></StaggerItem>
                        <StaggerItem>
                            <StatCard
                                icon={Package}
                                label="Inventory Items"
                                value={stats?.inventoryItems}
                                sub={stats?.inventoryValue ? `₹${(stats.inventoryValue / 100000).toFixed(1)}L value` : ''}
                                color="purple"
                                to="/staff/inventory"
                            />
                        </StaggerItem>
                        <StaggerItem>
                            <StatCard
                                icon={TrendingUp}
                                label="Staff Online"
                                value={`${stats?.activeStaff ?? 0}/${stats?.totalStaff ?? 0}`}
                                sub="Available right now"
                                color="green"
                                to="/staff/team"
                            />
                        </StaggerItem>
                    </StaggerContainer>
                )}

                {/* ── Main two-column area ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                    {/* Left column: My Tasks + Overdue */}
                    <div className="lg:col-span-2 space-y-5">

                        {/* My Tasks */}
                        <div className="bg-slate-900 rounded-xl border border-white/5">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <CheckSquare size={14} className="text-blue-400" />
                                    My Tasks
                                    <span className="text-slate-500 font-normal">({myTasks.length})</span>
                                </h2>
                                <Link to="/staff/tasks" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                    View all <ArrowRight size={11} />
                                </Link>
                            </div>
                            {myTasks.length === 0 ? (
                                <div className="px-4 py-8 text-center text-slate-500 text-sm">All clear — no pending tasks</div>
                            ) : (
                                <div className="divide-y divide-white/4">
                                    {myTasks.slice(0, 6).map(task => {
                                        const StatusIcon = STATUS_ICONS[task.status] || AlertCircle
                                        const isOverdue = task.due_date && task.due_date < today
                                        return (
                                            <div key={task.id} className="flex items-center gap-3 px-4 py-3">
                                                <button
                                                    onClick={() => cycleMut.mutate({ id: task.id, status: STATUS_CYCLE[task.status] || 'PENDING' })}
                                                    className={`p-1 rounded-md ${STATUS_COLORS[task.status]} border shrink-0 hover:opacity-70 transition`}
                                                    title={`→ ${STATUS_CYCLE[task.status]}`}
                                                >
                                                    <StatusIcon size={12} />
                                                </button>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm truncate ${task.status === 'COMPLETED' ? 'line-through text-slate-500' : 'text-white'}`}>{task.title}</p>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        {task.due_date && (
                                                            <span className={`text-[10px] ${isOverdue ? 'text-red-400' : 'text-slate-500'}`}>
                                                                Due {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                            </span>
                                                        )}
                                                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PRIORITY_DOT[task.priority] || 'bg-slate-500'}`} />
                                                        <span className="text-[10px] text-slate-500">{task.task_category}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Overdue Tasks */}
                        {overdueTasks.length > 0 && (
                            <div className="bg-slate-900 rounded-xl border border-red-500/20">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                                    <h2 className="text-sm font-semibold text-red-400 flex items-center gap-2">
                                        <AlertCircle size={14} />
                                        Overdue Tasks
                                        <span className="text-red-500/60 font-normal">({overdueTasks.length})</span>
                                    </h2>
                                    <Link to="/staff/tasks" className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                                        View all <ArrowRight size={11} />
                                    </Link>
                                </div>
                                <div className="divide-y divide-white/4">
                                    {overdueTasks.slice(0, 5).map(task => {
                                        const assignee = users.find(u => u.id === task.assigned_to)
                                        return (
                                            <div key={task.id} className="flex items-center gap-3 px-4 py-3">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm text-white truncate">{task.title}</p>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-[10px] text-red-400">
                                                            Due {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                        </span>
                                                        {assignee && (
                                                            <span className="text-[10px] text-slate-500 flex items-center gap-1">
                                                                <UserAvatar user={assignee} size={4} />
                                                                {assignee.name?.split(' ')[0]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-lg shrink-0 ${STATUS_COLORS[task.status]} border`}>
                                                    {task.status.replace('_', ' ')}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Follow-up Calls */}
                        {followUpCalls.length > 0 && (
                            <div className="bg-slate-900 rounded-xl border border-white/5">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                                    <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                                        <Phone size={14} className="text-amber-400" />
                                        Pending Follow-ups
                                        <span className="text-slate-500 font-normal">({followUpCalls.length})</span>
                                    </h2>
                                    <Link to="/staff/call-logs" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                        View all <ArrowRight size={11} />
                                    </Link>
                                </div>
                                <div className="divide-y divide-white/4">
                                    {followUpCalls.map(call => (
                                        <div key={call.id} className="flex items-center gap-3 px-4 py-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-white truncate">{call.caller_name || 'Unknown'}</p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-[10px] text-slate-400">{call.caller_number}</span>
                                                    {call.follow_up_date && (
                                                        <span className={`text-[10px] ${call.follow_up_date < today ? 'text-red-400' : 'text-slate-500'}`}>
                                                            → {new Date(call.follow_up_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {call.caller_number && (
                                                <a
                                                    href={`tel:${call.caller_number}`}
                                                    className="text-green-400 hover:text-green-300 p-1.5 rounded-lg bg-green-600/10 hover:bg-green-600/20 transition shrink-0"
                                                >
                                                    <Phone size={12} />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right column: Staff + Activity + Tickets */}
                    <div className="space-y-5">

                        {/* Staff Status */}
                        <div className="bg-slate-900 rounded-xl border border-white/5">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <Users size={14} className="text-green-400" />
                                    Staff Status
                                </h2>
                                <Link to="/staff/team" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                    Team <ArrowRight size={11} />
                                </Link>
                            </div>
                            <div className="p-3 space-y-1.5">
                                {users.length === 0 ? (
                                    <p className="text-slate-500 text-xs text-center py-4">No staff data</p>
                                ) : (
                                    users.slice(0, 8).map(u => (
                                        <div key={u.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/3 transition">
                                            <div className="relative shrink-0">
                                                <UserAvatar user={u} size={7} />
                                                <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-slate-900 ${STAFF_STATUS_COLORS[u.status] || 'bg-slate-500'}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-medium text-white truncate">{u.name}</p>
                                                <p className="text-[10px] text-slate-500 truncate">{u.role}</p>
                                            </div>
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 ${u.status === 'AVAILABLE' ? 'bg-green-500/15 text-green-400' :
                                                u.status === 'BUSY' ? 'bg-amber-500/15 text-amber-400' :
                                                    u.status === 'ON_LEAVE' ? 'bg-red-500/15 text-red-400' :
                                                        'bg-slate-500/15 text-slate-400'
                                                }`}>
                                                {(u.status || 'OFFLINE').replace('_', ' ')}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Open Tickets */}
                        {openTickets.length > 0 && (
                            <div className="bg-slate-900 rounded-xl border border-white/5">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                                    <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                                        <Ticket size={14} className="text-rose-400" />
                                        Open Tickets
                                        <span className="text-slate-500 font-normal">({openTickets.length})</span>
                                    </h2>
                                    <Link to="/staff/tickets" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                        All <ArrowRight size={11} />
                                    </Link>
                                </div>
                                <div className="divide-y divide-white/4">
                                    {openTickets.map(ticket => (
                                        <div key={ticket.id} className="px-4 py-3">
                                            <p className="text-xs text-white truncate">{ticket.title || ticket.subject || 'Untitled'}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                {ticket.priority && (
                                                    <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[ticket.priority] || 'bg-slate-500'}`} />
                                                )}
                                                <span className="text-[10px] text-slate-500">{timeAgo(ticket.created_at)}</span>
                                                {ticket.status && (
                                                    <span className="text-[10px] text-slate-400">{ticket.status}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Recent Activity Feed */}
                        <div className="bg-slate-900 rounded-xl border border-white/5">
                            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
                                <Activity size={14} className="text-blue-400" />
                                <h2 className="text-sm font-semibold text-white">Activity Feed</h2>
                            </div>
                            <div className="divide-y divide-white/4">
                                {recentActivity.length === 0 ? (
                                    <p className="text-slate-500 text-xs text-center py-6">No recent activity</p>
                                ) : (
                                    recentActivity.map((item, i) => {
                                        if (item.type === 'task') {
                                            const assignee = users.find(u => u.id === item.data.assigned_to)
                                            return (
                                                <div key={i} className="flex items-start gap-2.5 px-4 py-2.5">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-slate-300 truncate">
                                                            Task: <span className="text-white">{item.data.title}</span>
                                                        </p>
                                                        <p className="text-[10px] text-slate-500">
                                                            {assignee?.name?.split(' ')[0] || 'Unassigned'} · {timeAgo(item.time)}
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        if (item.type === 'call') {
                                            return (
                                                <div key={i} className="flex items-start gap-2.5 px-4 py-2.5">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-slate-300 truncate">
                                                            Call: <span className="text-white">{item.data.caller_name || item.data.caller_number}</span>
                                                        </p>
                                                        <p className="text-[10px] text-slate-500">{item.data.business_type || 'General'} · {timeAgo(item.time)}</p>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        if (item.type === 'ticket') {
                                            return (
                                                <div key={i} className="flex items-start gap-2.5 px-4 py-2.5">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-slate-300 truncate">
                                                            Ticket: <span className="text-white">{item.data.title || item.data.subject || 'New ticket'}</span>
                                                        </p>
                                                        <p className="text-[10px] text-slate-500">{timeAgo(item.time)}</p>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        return null
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </StaffLayout>
    )
}
